<template>
    <div class="converter-filters-wrapper prevent-auto-margins" :class="{ 'mobile': getMobileAppState }">
        <div class="align-elements-helper" :class="{'non-authenticated-feature': nonAuthenticatedFeature}">
            <!-- Currency Dropdown -->
            <dropdown-currency
            class="currency-dropdown"
            :params="currencyDropdownParams"
            :options="currencyListForDropdown"
            :selectedOption="converterFilters.currency"
            @clicked="selectedCurrencyHandler"/>
            <!-- Amount Input -->
            <input-amount
            class="amount-input-wrapper"
            :params="amountInputParams"
            :inputValue="converterFilters.originValue"
            @input="[converterFilters.originValue = $event, amountInputParams.errorState = false]"
            @onBlur="amountValidation"/>
            <!-- Calendar -->
            <input-calendar
            class="converter-calendar"
            :params="{ labelText: t('exchanges.converter.labels.exchangeDate') }"
            :disabledDates="{ from: new Date() }"
            :inputValue="converterFilters.quotationDate"
            @input="calendarInputHandler"/>
            <!-- Convert Button -->
            <button-primary
            class="convert-button"
            buttonSize="small"
            :label="t('exchanges.converter.labels.convert')"
            @clicked="convertButtontHandler"/>
        </div>
    </div>
</template>

<script setup>
import DropdownCurrency from '@/components/generic/dropdowns/currency/Currency'
import InputAmount from '@/components/generic/inputs/Amount'
import InputCalendar from '@/components/generic/inputs/Calendar'
import ButtonPrimary from '@/components/generic/buttons/Primary'
/* Vue */
import { ref, computed, watch, onMounted } from 'vue'
/* Aux */
import { flagCurrenciesIconMapper } from '@/assets/scripts/mappers/flags'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@nonAuthenticatedFeature {Boolean} to apply specific styles for nonAuthenticatedFeature
@appliedFilters {Object} - applied filters
@exchangeRatesTableList {Array} - exchange rates table list to parse for the currency dropdown
*/
const props = defineProps({
    nonAuthenticatedFeature: Boolean,
    appliedFilters: Object,
    exchangeRatesTableList: Array
})

const emit = defineEmits(['applyFilters'])

/* mapGetters */
const { getMobileAppState } = mapGetters()

/* Filters */
const converterFilters = ref({
    currency: 'AOA',
    originValue: '',
    quotationDate: new Date()
})

/* Amount Input */
const amountInputParams = ref({
    labelText: t('exchanges.converter.labels.amount'),
    errorState: false,
    errorMessage: t('mandatoryFieldError'),
    placeholder: '1,00'
})

const amountValidation = () => {
    if (converterFilters.value.originValue && Number(converterFilters.value.originValue) === 0) {
        amountInputParams.value.errorMessage = t('invalidAmount')
        amountInputParams.value.errorState = true
    }
}

/* Currency Dropdown */
const currencyDropdownParams = ref({
    labelText: t('exchanges.converter.labels.currency'),
    displayDescriptionOnHeader: true
})

/*************************************************
*                                                *
*                Currency Dropdown               *
*                                                *
*************************************************/
/* Returns an array with a exchange rates grouped by currency */
const groupByCurrency = computed(() => {
    const groupObject = props.exchangeRatesTableList.reduce((acc, item) => {
        acc[item.currency] = acc[item.currency] ?? []
        acc[item.currency].push(item)

        return acc
    }, {})

    return Object.getOwnPropertyNames(groupObject).map(attributeName => groupObject[attributeName])
})

const currencyListForDropdown = computed(() => {
    const groupedCurrencies = groupByCurrency.value.map(obj => ({
        id: obj[1].currency,
        currency: `${obj[1].currency} - ${obj[1].description}`,
        description: obj[1].currency,
        icon: `flags/${flagCurrenciesIconMapper(obj[1].currency)}`
    }))

    return [
        {
            id: 'AOA',
            currency: 'AOA - Kwanza',
            description: 'AOA',
            icon: `flags/${flagCurrenciesIconMapper('AKZ')}`
        },
        ...groupedCurrencies
    ]
})

/*************************************************
*                                                *
*                     Handlers                   *
*                                                *
*************************************************/
const mobileEmitFilters = () => {
    if (getMobileAppState.value) emit('applyFilters', converterFilters.value)
}

const calendarInputHandler = event => {
    converterFilters.value.quotationDate = event

    mobileEmitFilters()
}

const selectedCurrencyHandler = event => {
    converterFilters.value = {
        ...converterFilters.value,
        currency: event
    }

    mobileEmitFilters()
}

const convertButtontHandler = () => {
    amountInputParams.value.errorState = false

    if (!Number(converterFilters.value.originValue)) {
        amountInputParams.value.errorState = true
        amountInputParams.value.errorMessage = !converterFilters.value.originValue ? t('mandatoryFieldError') : t('invalidAmount')
        return
    }

    emit('applyFilters', converterFilters.value)
}

watch(() => converterFilters.value.originValue, () => {
    if (!converterFilters.value.originValue) converterFilters.value.originValue = 0
})

onMounted(() => {
    converterFilters.value = props.appliedFilters
})

</script>

<style lang="scss" scoped>
.converter-filters-wrapper {
    @include cardGeneral1;
    width: 100%;
    padding: 16px 16px 4px 16px; // the bottom padding will join the 12px of the min-height in the generic input bottom elements (12 + 4 = 16)

    .align-elements-helper {
        width: 100%;

        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 16px;

        &.non-authenticated-feature {
            margin: 0 auto;
        }
    }

    .currency-dropdown {
        width: 116px;

        :deep(.dropdown-animation-wrapper) {
            width: 300px;
            left: 0;
        }
    }

    .amount-input-wrapper {
        width: 186px;
    }

    .converter-calendar {
        width: 144px;
    }

    .convert-button {
        min-width: 116px;
        margin-top: 24px;
    }

    &.mobile {
        padding-bottom: 24px;

        .align-elements-helper {
            flex-wrap: wrap;
        }

        .amount-input-wrapper {
            width: calc(100% - 132px);
        }

        .converter-calendar {
            width: 100%;
        }

        .convert-button {
            width: 100%;
            margin-top: 8px;
        }
    }
}
</style>
